import { fecha, numero, useTraduccion, type Idioma } from '@tallerya/i18n';
import { CampoNumero, CampoSeleccion, CampoTexto, Fila, type EstadoFormulario } from '@tallerya/ui';
import { diaLocal } from '../gastos/datos';
import type { Campo } from './datos';

type Traducir = ReturnType<typeof useTraduccion>['t'];

// **La promesa del producto** (#119): los campos del detalle se arman desde lo
// que declara el tipo de activo. Un tipo nuevo con sus campos genera su
// formulario sin tocar código.
//
// Cada campo vive en el formulario como `details.<clave>`, que es **el mismo
// nombre que usa la API para sus errores**: un «requerido» o un «no es una
// opción» cae solo bajo su campo.
//
// Todo se guarda como texto, también el sí/no —«true», «false» o vacío—: así el
// formulario tiene un solo tipo de valor, y el sí/no puede quedar sin dato, que
// es lo que pide un campo opcional. Al guardar se convierte (`aDetalle`).

/** El nombre de un campo del detalle en el formulario y en los errores de la API. */
export const nombreDe = (clave: string) => `details.${clave}`;

interface Props {
  form: EstadoFormulario<Record<string, string>>;
  /** Solo los activos, en su orden. */
  campos: readonly Campo[];
}

export function CamposDelTipo({ form, campos }: Props) {
  const { t } = useTraduccion();

  // De a dos por fila: los campos del detalle suelen ser cortos.
  const filas: Campo[][] = [];
  for (let i = 0; i < campos.length; i += 2) filas.push(campos.slice(i, i + 2));

  return (
    <>
      {filas.map((fila) => (
        <Fila key={fila.map((c) => c.code).join('|')}>
          {fila.map((campo) => (
            <CampoDelTipo key={campo.code} form={form} campo={campo} sinDato={t('admin.activos.sinDato')} />
          ))}
        </Fila>
      ))}
    </>
  );
}

function CampoDelTipo({
  form,
  campo,
  sinDato,
}: {
  form: EstadoFormulario<Record<string, string>>;
  campo: Campo;
  sinDato: string;
}) {
  const { t } = useTraduccion();
  const base = { form, nombre: nombreDe(campo.code), etiqueta: campo.label, obligatorio: campo.isRequired };

  switch (campo.valueType) {
    case 'number':
      // Sin tope de decimales: el tipo no lo declara, y la API guarda un `decimal`.
      return <CampoNumero {...base} />;
    case 'date':
      return <CampoTexto {...base} tipo="date" />;
    case 'bool':
      return (
        <CampoSeleccion
          {...base}
          placeholder={sinDato}
          opciones={[
            { valor: 'true', etiqueta: t('admin.activos.si') },
            { valor: 'false', etiqueta: t('admin.activos.no') },
          ]}
        />
      );
    case 'list':
      return (
        <CampoSeleccion
          {...base}
          placeholder={sinDato}
          opciones={(campo.allowedValues ?? []).map((o) => ({ valor: o.value, etiqueta: o.label }))}
        />
      );
    default:
      return <CampoTexto {...base} autoComplete="off" />;
  }
}

/**
 * Cómo se lee un valor del detalle fuera del formulario —en la ficha y en su
 * historial (#187)—: «Sí», «84.500», «1 oct 2026», «Diésel». Llega como lo
 * escribe la API, `true` o `"true"` según de dónde venga, y por eso se pasa a
 * texto primero. Una opción que el tipo ya no tiene sale con su código.
 */
export function leerDetalle(campo: Campo, valor: string | number | boolean, t: Traducir, idioma: Idioma): string {
  const texto = String(valor);

  switch (campo.valueType) {
    case 'bool':
      return texto === 'true' ? t('admin.activos.si') : t('admin.activos.no');
    case 'number':
      return Number.isFinite(Number(texto)) ? numero(Number(texto), idioma) : texto;
    case 'date':
      // `diaLocal` y no `new Date(texto)`: un día suelto leído como medianoche UTC
      // se mostraría el día anterior al oeste de Greenwich.
      return /^\d{4}-\d{2}-\d{2}$/.test(texto) ? fecha(diaLocal(texto), idioma) : texto;
    case 'list':
      return campo.allowedValues?.find((o) => o.value === texto)?.label ?? texto;
    default:
      return texto;
  }
}

/** El valor de un campo como lo guarda el formulario: texto, o vacío si no tiene. */
export function aTexto(valor: string | number | boolean | undefined): string {
  return valor === undefined ? '' : String(valor);
}

/**
 * Lo que va en `details` al guardar: solo los campos activos del tipo y con
 * dato, cada uno con su tipo de verdad. Un campo vacío no se manda: es «no
 * tiene». Lo que el activo tenía en campos apagados no se manda; la API lo
 * conserva.
 */
export function aDetalle(
  valores: Record<string, string>,
  campos: readonly Campo[],
): Record<string, string | number | boolean> {
  const detalle: Record<string, string | number | boolean> = {};

  for (const campo of campos) {
    const valor = (valores[nombreDe(campo.code)] ?? '').trim();
    if (valor === '') continue;

    detalle[campo.code] =
      campo.valueType === 'number' ? Number(valor) : campo.valueType === 'bool' ? valor === 'true' : valor;
  }

  return detalle;
}

/**
 * Lo que la pantalla puede decir antes de ir a la API: un obligatorio vacío, un
 * número que no es número. Lo demás —una opción que ya no existe, una fecha
 * imposible— lo dice la API, y cae en su campo igual.
 */
export function erroresDelDetalle(
  valores: Record<string, string>,
  campos: readonly Campo[],
): Record<string, string> {
  const errores: Record<string, string> = {};

  for (const campo of campos) {
    const valor = (valores[nombreDe(campo.code)] ?? '').trim();

    if (valor === '') {
      if (campo.isRequired) errores[nombreDe(campo.code)] = 'validation.required';
    } else if (campo.valueType === 'number' && !Number.isFinite(Number(valor))) {
      errores[nombreDe(campo.code)] = 'validation.invalid';
    }
  }

  return errores;
}
